import type { Metric } from 'web-vitals';

import {
  getPerformanceConsentGeneration,
  recordPerformanceTrace,
} from './performance-client';
import { resolveAnalyticsPagePath } from './routes';
import { getPerformanceReleaseAttributes } from './performance-config';

type WebVitalsModule = typeof import('web-vitals');
type Subscription = { generation: number };

const TRACE_NAMES = {
  FCP: 'web_fcp',
  LCP: 'web_lcp',
  TTFB: 'web_ttfb',
  FID: 'web_fid',
  CLS: 'web_cls',
} as const;

let modulesPromise: Promise<WebVitalsModule> | null = null;
let activeSubscription: Subscription | null = null;
let installedGeneration: number | null = null;

/**
 * Record one sample per metric for this document's first consenting generation.
 * Read value_us (timings) or value_millionths (CLS), never trace duration: the
 * SDK requires a positive duration, so its 1 ms envelope also preserves zeroes.
 * Revoking then granting consent requires a new document for fresh observers.
 */
export const startPerformanceWebVitals = (): (() => void) => {
  const generation = getPerformanceConsentGeneration();
  if (
    generation === null ||
    typeof window === 'undefined' ||
    typeof window.PerformanceObserver !== 'function'
  ) {
    return () => {};
  }

  const subscription: Subscription = { generation };
  activeSubscription = subscription;
  const view = resolveAnalyticsPagePath(window.location.pathname) ?? '/other';
  const timeOrigin = window.performance.timeOrigin;

  modulesPromise ??= import('web-vitals');
  void modulesPromise.then(vitals => {
    if (
      activeSubscription !== subscription ||
      getPerformanceConsentGeneration() !== generation ||
      installedGeneration !== null
    ) {
      return;
    }

    installedGeneration = generation;
    const reported = new Set<Metric['name']>();
    const report = (metric: Metric) => {
      if (
        activeSubscription?.generation !== generation ||
        getPerformanceConsentGeneration() !== generation ||
        !Object.prototype.hasOwnProperty.call(TRACE_NAMES, metric.name) ||
        reported.has(metric.name) ||
        !Number.isFinite(metric.value) ||
        metric.value < 0
      ) {
        return;
      }

      const value = Math.round(metric.value * (metric.name === 'CLS' ? 1_000_000 : 1000));
      if (!Number.isSafeInteger(value)) {
        return;
      }

      const inputStart = metric.entries[0]?.startTime;
      const hasTimeOrigin = Number.isFinite(timeOrigin) && timeOrigin > 0;
      const startTime = metric.name === 'CLS' || !hasTimeOrigin
        ? Date.now()
        : metric.name === 'FID' && typeof inputStart === 'number' && Number.isFinite(inputStart) && inputStart >= 0
          ? timeOrigin + inputStart
          : timeOrigin;

      reported.add(metric.name);
      recordPerformanceTrace({
        name: TRACE_NAMES[metric.name],
        startTime,
        duration: 1,
        attributes: { metric: metric.name, view, ...getPerformanceReleaseAttributes() },
        metrics: { [metric.name === 'CLS' ? 'value_millionths' : 'value_us']: value },
      }, generation);
    };

    // Unsupported individual metrics must not prevent the others from working.
    for (const getter of [vitals.getFCP, vitals.getLCP, vitals.getTTFB, vitals.getFID, vitals.getCLS]) {
      try {
        getter(report);
      } catch {
        // Optional browser measurements must never affect the console.
      }
    }
  }).catch(() => {
    // Keep a failed import cached; console requests must not trigger retry loops.
  });

  return () => {
    if (activeSubscription === subscription) {
      activeSubscription = null;
    }
  };
};
